import { observer } from 'mobx-react';
import { UNSAFE_PortalProvider } from 'react-aria';
import type { Key } from 'react-aria-components';
import { ComboBox, Input, Label, ListBox, ListBoxItem, Popover } from 'react-aria-components/ComboBox';

import { OrganizationData } from '../../../../../app/data/model';
import IconButton from '../../../../../app/shared/components/Button/IconButton';
import Icon from '../../../../../app/shared/components/Icon/Icon';
import InputWithLoader from '../../../../../app/shared/components/Input/InputWithLoader';
import StoreDisplayName from '../../StoreDisplayName/StoreDisplayName';
import StoreSelectorState from '../StoreSelectorState';
import css from './StoreSelectorSearch.css';

interface StoreSelectorSearchProps {
  state: StoreSelectorState;
  // The listbox Popover portals into this element (the selector subtree) so an
  // item click stays inside StoreSelector's useOnClickOutside ref — otherwise
  // selecting a result would close the whole selector before selectStore runs.
  popoverContainer?: HTMLElement | null;
}

const StoreSelectorSearch = ({ state, popoverContainer }: StoreSelectorSearchProps) => {
  const handleChange = (key: Key | null) => {
    if (key != null) {
      state.selectStore(String(key));
    }
  };

  return (
    <UNSAFE_PortalProvider getContainer={() => popoverContainer ?? null}>
      <ComboBox
        className={css.root}
        inputValue={state.searchPhrase}
        items={state.searchResults}
        onInputChange={state.setSearchPhrase}
        onChange={handleChange}
        allowsCustomValue={false}
        allowsEmptyCollection
        menuTrigger="input"
      >
        <Label className="visuallyHidden">{state.translation.searchPlaceholder}</Label>
        <InputWithLoader isLoading={state.isSearching} loaderContainerClassName={css.searchBox}>
          <Icon name="search" className={css.icon} />
          <Input className={css.input} placeholder={state.translation.searchPlaceholder} />
          {state.searchPhrase && !state.isSearching && (
            <IconButton className={css.clear} icon={<Icon name="cross" />} onClick={() => state.setSearchPhrase('')}>
              <span className="visuallyHidden">{state.translation.clearSearchButton}</span>
            </IconButton>
          )}
        </InputWithLoader>
        <Popover className={css.results} offset={4} placement="bottom start" shouldFlip={false}>
          <>
            {state.isNoResultsMessageVisible && (
              <p className={css.noResultsText}>{state.translation.noSearchResultsText}</p>
            )}
            <ListBox>
              {(org: OrganizationData) => (
                <ListBoxItem id={org.id} textValue={org.name} className={css.item}>
                  <StoreDisplayName storeId={org.id} storeName={org.name} />
                </ListBoxItem>
              )}
            </ListBox>
          </>
        </Popover>
      </ComboBox>
    </UNSAFE_PortalProvider>
  );
};

export default observer(StoreSelectorSearch);
